import React, { useEffect, useState } from 'react';
import { format, addMonths, startOfYear, eachMonthOfInterval, isSameDay, isToday, parseISO, subMonths } from 'date-fns';
import { Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MiniCalendar } from './MiniCalendar.tsx';
import { MonthCalendar } from './MonthCalendar.tsx';
import { CalendarHeader } from './CalendarHeader.tsx';
import { AddEventModal } from './AddEventModal.tsx';
import { Task } from '../kanban/types';
import { getTasks } from '../../../utils/tasks.ts';
import { getCalenderEvents, saveCalenderEvent } from '../../../utils/calender.ts';
import { CalendarEvent } from './types';
import { useAgentIntegration } from '../../../hooks/useAgentIntegration.ts';

export const CalendarView: React.FC = () => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'tasks' | 'events'>('all');
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [currentDateTime, setCurrentDateTime] = useState(currentDate);
  
  // New state for highlighting newly created events
  const [newEventId, setNewEventId] = useState<string | null>(null);
  const [showEventSparkles, setShowEventSparkles] = useState(false);

  // Agent integration
  const { recentActions, refreshTrigger, markActionAsViewed } = useAgentIntegration('calendar');

  // Check for new event data from chat navigation
  useEffect(() => {
    const checkForNewEvent = () => {
      const newEventData = sessionStorage.getItem('newEventData');
      console.log('🌟 CalendarView - Checking for new event data:', newEventData);
      
      if (newEventData) {
        try {
          const eventData = JSON.parse(newEventData);
          console.log('🌟 CalendarView - Found new event data from chat:', eventData);
          
          if (eventData.shouldHighlight && eventData.eventId) {
            // Convert eventId to string for consistency
            const eventId = eventData.eventId.toString();
            console.log('🌟 CalendarView - Setting up highlighting for event ID:', eventId);
            
            setNewEventId(eventId);
            setShowEventSparkles(true);
            
            console.log('🌟 CalendarView - State set:', {
              newEventId: eventId,
              showEventSparkles: true
            });
            
            // Auto-navigate to the month containing the new event
            if (eventData.startTime) {
              const eventDate = new Date(eventData.startTime);
              console.log('🌟 CalendarView - Auto-selecting month for new event:', eventDate);
              setSelectedMonth(eventDate);
              
              // Also update the selected year to match the event
              setSelectedYear(eventDate.getFullYear());
            } else {
              // Fallback to current month if no startTime
              console.log('🌟 CalendarView - No startTime found, auto-selecting current month');
              setSelectedMonth(currentDate);
              setSelectedYear(currentDate.getFullYear());
            }
            
            // Clear the session storage after a delay to ensure event is fetched
            setTimeout(() => {
              sessionStorage.removeItem('newEventData');
              console.log('🌟 CalendarView - Cleared newEventData from sessionStorage');
            }, 2000);
            
            // Hide sparkles after 4 seconds (longer to ensure visibility)
            setTimeout(() => {
              console.log('🌟 CalendarView - Hiding event sparkles');
              setShowEventSparkles(false);
            }, 4000);
            
            // Remove highlighting after 8 seconds (longer to ensure user sees it)
            setTimeout(() => {
              console.log('🌟 CalendarView - Removing event highlighting');
              setNewEventId(null);
            }, 8000);
          }
        } catch (error) {
          console.error('Error parsing new event data:', error);
        }
      }
    };

    // Check immediately on mount
    checkForNewEvent();
    
    // Also check periodically for a few seconds in case navigation is delayed
    const interval = setInterval(checkForNewEvent, 500);
    
    // Clear interval after 5 seconds
    setTimeout(() => {
      clearInterval(interval);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Update current time every minute
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const fetchTasksAndCalendarEvents = async () => {
    console.log('📅 CalendarView - Fetching tasks and calendar events...');
    const tasks = await getTasks();
    const calEvents = await getCalenderEvents();
    console.log('📅 CalendarView - Fetched', tasks?.length || 0, 'tasks and', calEvents?.length || 0, 'events');
    console.log('📅 CalendarView - All calendar events:', calEvents?.map(e => ({ id: e.id, title: e.title })));
    
    setAllTasks(tasks || []);
    setCalendarEvents(calEvents || []);
    
    // Log if we found the highlighted event
    if (newEventId) {
      const foundEvent = calEvents?.find(event => event.id.toString() === newEventId);
      if (foundEvent) {
        console.log('🌟 CalendarView - Found highlighted event in fetched data:', foundEvent);
      } else {
        console.log('🌟 CalendarView - Highlighted event not found in fetched data, eventId:', newEventId);
        console.log('🌟 CalendarView - Available event IDs:', calEvents?.map(e => e.id));
      }
    }
  };

  useEffect(() => {
    fetchTasksAndCalendarEvents();
  }, [refreshTrigger]);

  // Re-fetch events when a new event is highlighted to ensure it's included
  useEffect(() => {
    if (newEventId) {
      console.log('🌟 New event highlighted, re-fetching to ensure it\'s included:', newEventId);
      // Small delay to ensure the event was saved in the backend
      setTimeout(() => {
        fetchTasksAndCalendarEvents();
      }, 1000);
    }
  }, [newEventId]); // Refetch when agent actions occur
  
  // Handle recent agent actions
  useEffect(() => {
    if (recentActions.length > 0) {
      // Show a brief highlight or animation for new events
      recentActions.forEach(action => {
        if (action.type === 'calendar' && !action.executed) {
          // Highlight the new event for a few seconds
          setTimeout(() => {
            markActionAsViewed(action.data.id);
          }, 3000);
        }
      });
    }
  }, [recentActions, markActionAsViewed]);

  const startDate = startOfYear(new Date(selectedYear, 0, 1));
  const months = eachMonthOfInterval({
    start: startDate,
    end: addMonths(startDate, 11)
  });

  const handleAddEvent = async (event: CalendarEvent) => {
    try {
      setCalendarEvents(prev => [...prev, event]);
      await saveCalenderEvent(event);
      setIsAddEventModalOpen(false);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    console.log('📅 CalendarView - getEventsForDate called for date:', format(date, 'yyyy-MM-dd'));
    let items: CalendarEvent[] = [];

    // Add events based on filter
    if (selectedFilter === 'all' || selectedFilter === 'events') {
      const events = calendarEvents.filter(event => {
        const eventDate = event.startTime ? new Date(event.startTime) : null;
        const matches = (
          eventDate &&
          eventDate.getFullYear() === date.getFullYear() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getDate() === date.getDate()
        );
        
        if (matches) {
          console.log('📅 CalendarView - Event matches date:', {
            eventId: event.id,
            eventTitle: event.title,
            eventDate: eventDate?.toISOString(),
            queryDate: date.toISOString()
          });
        }
        
        return matches;
      });
      items = [...items, ...events];
    }

    // Add tasks based on filter
    if (selectedFilter === 'all' || selectedFilter === 'tasks') {
      const tasks = allTasks.filter(task => {
        if (!task.dueDate) return false;
        return format(new Date(task.dueDate), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
      });
      
      const taskEvents: CalendarEvent[] = tasks.map(task => ({
        id: task.id.toString(),
        title: task.name,
        description: task.description,
        date: format(new Date(task.dueDate!), 'yyyy-MM-dd'),
        isAllDay: true,
        priority: task.priority || 'MEDIUM',
        isTask: true // Add this flag to differentiate tasks from events
      }));
      
      items = [...items, ...taskEvents];
    }

    console.log('📅 CalendarView - getEventsForDate returning:', {
      date: format(date, 'yyyy-MM-dd'),
      itemsCount: items.length,
      items: items.map(item => ({ id: item.id, title: item.title }))
    });

    return items;
  };

  const getPriorityColor = (priority: 'LOW' | 'MEDIUM' | 'HIGH') => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'MEDIUM':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleYearChange = (direction: 1 | -1) => {
    setSelectedYear(prev => prev + direction);
  };

  const handleMonthChange = (direction: 1 | -1) => {
    if (selectedMonth) {
      const newMonth = direction === 1 
        ? addMonths(selectedMonth, 1)
        : subMonths(selectedMonth, 1);
      setSelectedMonth(newMonth);
    }
  };

  const isCurrentDay = (date: Date) => {
    return isSameDay(date, currentDateTime);
  };

  const renderMiniCalendar = (month: Date) => {
    return (
      <div
        key={month.toISOString()}
        onClick={() => setSelectedMonth(month)}
        className="cursor-pointer transform transition-all duration-200 hover:scale-[1.02] rounded-xl overflow-hidden
          bg-white/80 backdrop-blur-md border border-gray-200/50
          shadow-sm hover:shadow-md hover:bg-white/90"
      >
        <div className="text-center mb-2 bg-gray-50/50 py-3 px-2">
          <h3 className={`text-sm font-semibold transition-colors ${
            month.getMonth() === currentDateTime.getMonth() && 
            month.getFullYear() === currentDateTime.getFullYear() 
              ? 'text-gray-900'
              : 'text-gray-700'
          }`}>
            {format(month, 'MMMM yyyy')}
          </h3>
        </div>
        <div className="px-2 pb-2">
          <MiniCalendar 
            month={month} 
            events={calendarEvents}
            isCurrentDay={isCurrentDay}
            getEventsForDate={getEventsForDate}
            getPriorityColor={getPriorityColor}
            newEventId={newEventId}
            showEventSparkles={showEventSparkles}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <div className="relative z-10 h-screen overflow-hidden">
        {/* Simplified Header */}
        <div className="sticky top-0 z-20 mb-8">
          <div className="bg-white/95 backdrop-blur-lg border-b border-gray-200/60">
            <div className="p-6 lg:p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                      Calendar
                    </h1>
                    <p className="text-gray-600 mt-2 text-lg">
                      Manage your wedding timeline and events
                    </p>
                  </div>
                  
                  {/* New Event Indicator */}
                  <AnimatePresence>
                    {newEventId && showEventSparkles && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        className="flex items-center space-x-2 bg-gradient-to-r from-green-100 to-emerald-100 px-3 py-2 rounded-full border border-green-200"
                      >
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        >
                          <Sparkles className="w-4 h-4 text-green-600" />
                        </motion.div>
                        <span className="text-sm font-medium text-green-800">
                          New event added!
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <CalendarHeader
                  selectedFilter={selectedFilter}
                  onFilterChange={setSelectedFilter}
                  onAddEvent={() => setIsAddEventModalOpen(true)}
                  onYearChange={handleYearChange}
                  onMonthChange={handleMonthChange}
                  selectedYear={selectedYear}
                  isMonthView={!!selectedMonth}
                />
              </div>
            </div>
          </div>
        </div>
        
        {selectedMonth ? (
          <div className="h-[calc(100vh-12rem)] px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setSelectedMonth(null)}
                className="text-gray-600 hover:text-gray-900 flex items-center text-base lg:text-lg 
                  transition-all duration-200 group"
              >
                <span className="transform transition-transform group-hover:-translate-x-1">←</span>
                <span className="ml-2">Back to Year View</span>
              </button>
              
              {/* New Event Indicator for Month View */}
              <AnimatePresence>
                {newEventId && showEventSparkles && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    className="flex items-center space-x-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 py-2 rounded-full border border-green-200 shadow-lg"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <Sparkles className="w-5 h-5 text-green-600" />
                    </motion.div>
                    <span className="text-sm font-medium text-green-800">
                      New event added to calendar!
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="h-[calc(100%-6rem)]">
              <MonthCalendar 
                month={selectedMonth} 
                events={calendarEvents}
                isCurrentDay={isCurrentDay}
                getEventsForDate={getEventsForDate}
                getPriorityColor={getPriorityColor}
                newEventId={newEventId}
                showEventSparkles={showEventSparkles}
              />
            </div>
          </div>
        ) : (
          <div className="px-6 lg:px-8 pb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 
              max-h-[calc(100vh-16rem)] overflow-y-auto">
              {months.map(renderMiniCalendar)}
            </div>
          </div>
        )}

        <AddEventModal
          isOpen={isAddEventModalOpen}
          onClose={() => setIsAddEventModalOpen(false)}
          onAddEvent={handleAddEvent}
        />
      </div>
    </div>
  );
};

export default CalendarView;